import { useToast } from "@apex/component-library";
import axios, { AxiosError } from "axios";
import { useQuery, UseQueryOptions } from "react-query";

import { getIsMasterSubAccount } from "../components/MarginAccountDetailsTabs/BuyingPowerTab/utils/balanceRollupTools";
import { useBalanceRollupStore } from "../hooks/useBalanceRollupStore";
import { getAppConfigSetting } from "../utils/getAppConfigSetting";
import { GET_MASTER_SUB_ACCOUNT_ROLLUP_API } from "./constants";
import queryKeys from "./queryKeys";

/*
 *  The converted fields are the correct values for this application.
 *  This is because they are converted to USD no matter the currency of origin
 *  convertedTradeBalance: number;
 *  convertedSettleBalance: number;
 */

export type RollupLineItem = {
  processDate: string;
  accountNumber: string;
  firm: number;
  currencyCode: string;
  tradeBalance: number;
  settleBalance: number;
  convertedCurrency: string;
  convertedTradeBalance: number;
  convertedSettleBalance: number;
  conversionFactor: number;
  isMasterAccount: boolean;
  isSubAccount: boolean;
};
export interface BalanceRolledUpResponse {
  [key: string]: RollupLineItem[];
}

type Input = {
  accountNumber?: string;
  processDate?: string;
};

export function useGetBalanceRollupDetails(
  { accountNumber, processDate }: Input,
  options?: UseQueryOptions<BalanceRolledUpResponse, AxiosError>
) {
  const toast = useToast();
  const setIsMasterSubAccount = useBalanceRollupStore(
    state => state.setIsMasterSubAccount
  );

  const apiUrl = `${getAppConfigSetting(
    "apiUrl"
  )}${GET_MASTER_SUB_ACCOUNT_ROLLUP_API}`;

  const getUrl = (accountNumber: string, processDate?: string) =>
    `${apiUrl}/${accountNumber}${
      processDate
        ? `?processDate=${processDate}&rolledup=true`
        : "?rolledup=true"
    }`;

  const fetcher = async () => {
    if (!accountNumber) {
      return null;
    }
    const { data } = await axios.get(getUrl(accountNumber, processDate), {
      withCredentials: true,
    });
    return data;
  };

  return useQuery<BalanceRolledUpResponse, AxiosError>(
    [queryKeys.getBallanceRollup, accountNumber, processDate],
    fetcher,
    {
      enabled: !!accountNumber,
      onSuccess: data => setIsMasterSubAccount(getIsMasterSubAccount(data)),
      onError: error => {
        const status = error.response?.status || error.request.status;
        if (status >= 500) {
          toast({
            title: "System Error",
            status: "error",
            duration: null,
            isClosable: true,
            description:
              "Sorry, there was an error loading the Balances Rollup data.",
          });
        }
      },
      ...options,
    }
  );
}
